import { useState } from "react";
import { Calendar, Clock, User, Phone, Scissors, MessageSquare, Send, Sparkles } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/salonData";
import { openWhatsApp, buildAppointmentMessage } from "@/lib/whatsapp";

const QUICK_SERVICES = [
  "Bridal Makeup",
  "HD Party Makeup",
  "Hydra Facial",
  "Hair Spa",
  "Pre-Bridal Package",
  "Keratin Treatment",
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    date: "",
    time: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10)
      errs.phone = "Valid phone required";
    if (!form.service.trim()) errs.service = "Pick a service";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    openWhatsApp(buildAppointmentMessage(form));
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-[#fff5f8]" />

      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="heading-flourish">Reserve Your Glow</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-tight">
            Book Your <span className="text-gradient-rose italic">Appointment</span>
          </h2>
          <p className="mt-4 text-[#5c4642] text-base md:text-lg">
            Fill in your details — we'll instantly send your request to our
            WhatsApp for fast confirmation.
          </p>
        </div>

        {/* Quick services */}
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {QUICK_SERVICES.map((q) => (
            <button
              key={q}
              data-testid={`quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => update("service", q)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                form.service === q
                  ? "btn-rose"
                  : "bg-white border border-[#d98a99]/25 text-[#5c4642] hover:border-[#d98a99] hover:text-[#b56477]"
              }`}
            >
              {q}
            </button>
          ))}
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="mt-10 glass rounded-3xl p-6 md:p-10 shadow-[0_30px_60px_-20px_rgba(217,138,153,0.25)]"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field
              label="Your Name"
              icon={User}
              error={errors.name}
            >
              <input
                data-testid="form-name"
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Riya Sharma"
                className="form-input"
              />
            </Field>

            <Field label="Phone Number" icon={Phone} error={errors.phone}>
              <input
                data-testid="form-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="98XXXXXXXX"
                className="form-input"
              />
            </Field>

            <Field label="Service" icon={Scissors} error={errors.service} className="md:col-span-2">
              <select
                data-testid="form-service"
                value={form.service}
                onChange={(e) => update("service", e.target.value)}
                className="form-input"
              >
                <option value="">Select a service</option>
                <optgroup label="Popular">
                  {QUICK_SERVICES.map((q) => (
                    <option key={`q-${q}`} value={q}>
                      {q}
                    </option>
                  ))}
                </optgroup>
                {SERVICE_CATEGORIES.filter(
                  (c) => c !== "All" && c !== "Pre-Bridal" && c !== "Academy"
                ).map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {SERVICES.filter((s) => s.category === cat).map((s) => (
                      <option key={`${cat}-${s.name}`} value={s.name}>
                        {s.name} — ₹{s.price}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field label="Preferred Date" icon={Calendar}>
              <input
                data-testid="form-date"
                type="date"
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="form-input"
              />
            </Field>

            <Field label="Preferred Time" icon={Clock}>
              <input
                data-testid="form-time"
                type="time"
                value={form.time}
                onChange={(e) => update("time", e.target.value)}
                className="form-input"
              />
            </Field>

            <Field label="Message (optional)" icon={MessageSquare} className="md:col-span-2">
              <textarea
                data-testid="form-message"
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us anything — e.g. occasion, allergies, preferences..."
                className="form-input resize-none"
              />
            </Field>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-[#5c4642]">
              <Sparkles size={14} className="text-[#d4af37]" />
              Your request opens directly in WhatsApp.
            </div>
            <button
              type="submit"
              data-testid="booking-submit"
              className="btn-rose px-7 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2"
            >
              <Send size={16} />
              Book Appointment
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 12px 14px;
          background: white;
          border: 1px solid rgba(217, 138, 153, 0.25);
          border-radius: 12px;
          font-size: 14px;
          color: #2a1b18;
          font-family: inherit;
          transition: all 0.25s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #d98a99;
          box-shadow: 0 0 0 4px rgba(217, 138, 153, 0.12);
        }
        .form-input::placeholder { color: #5c4642aa; }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wider font-semibold text-[#b56477]">
        {Icon && <Icon size={12} />} {label}
      </div>
      {children}
      {error && (
        <div className="text-xs text-red-500 mt-1.5 font-medium">{error}</div>
      )}
    </label>
  );
}
